import { useEffect, useRef, useState } from 'react'

import Background from '@/components/Background/Background'
import Logo from '@/components/Logo/Logo'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { DISPLAY_STATE } from '@/types'
import { POSITION_ANIMATIONS } from '@/utils/scorebugPosition'

import GoalSportBar from './GoalSportBar'
import GoalSportBug from './GoalSportBug'
import './GoalSportTemplate.css'

import type { JsonMatchData, ScoreBugPosition } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

const ANIMATION_DURATION = 500 // ms — matches useTemplateAnimation duration

export interface GoalSportTemplateProps {
  matchData?: MatchData
  customerId: string
  eventGuid: string
  jsonData: JsonMatchData | null
  displayState: DISPLAY_STATE
  scoreBugPosition: ScoreBugPosition
  isVisible?: boolean
}

const GoalSportTemplate: FC<GoalSportTemplateProps> = ({
  matchData,
  jsonData,
  displayState,
  scoreBugPosition,
  isVisible = true,
}) => {
  const matchConfig = matchData || {}

  // Get home and away competitors
  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  // Use JSON data for team names, fallback to API data
  const homeTeam = homeCompetitor?.name || matchConfig.homeTeamConfig?.name || 'Home Team'
  const awayTeam = awayCompetitor?.name || matchConfig.awayTeamConfig?.name || 'Away Team'

  const targetPosition = scoreBugPosition

  // Reposition animation: exit old position → apply new position → enter
  const [activePosition, setActivePosition] = useState<ScoreBugPosition>(targetPosition)
  const [bugVisible, setBugVisible] = useState(true)
  const isRepositioning = useRef(false)

  useEffect(() => {
    if (targetPosition === activePosition || isRepositioning.current) return

    const exitAnim = POSITION_ANIMATIONS[activePosition].out
    const enterAnim = POSITION_ANIMATIONS[targetPosition].in
    console.log(`[Scorebug] position: ${activePosition} → ${targetPosition} | exit: ${exitAnim} | enter: ${enterAnim}`)

    isRepositioning.current = true
    setBugVisible(false)

    const timer = setTimeout(() => {
      setActivePosition(targetPosition)
      setBugVisible(true)
      isRepositioning.current = false
    }, ANIMATION_DURATION)

    return () => {
      clearTimeout(timer)
      isRepositioning.current = false
    }
  }, [targetPosition, activePosition])

  const effectiveBugVisible = isVisible && bugVisible

  return (
    <Background>
      <div className="goal-sport-container">
        {/* Top section with logos */}
        <div className="goal-sport-logo-container">
          <Logo matchConfig={matchConfig} slotId={1} teamName={homeTeam} />
          <Logo matchConfig={matchConfig} slotId={2} teamName={awayTeam} />
        </div>

        {/* Render Bug or Bar based on display state */}
        {displayState === DISPLAY_STATE.SCOREBAR ? (
          <GoalSportBar matchData={matchData} jsonData={jsonData} isVisible={isVisible} />
        ) : (
          <GoalSportBug
            matchData={matchData}
            jsonData={jsonData}
            isVisible={effectiveBugVisible}
            scorebugPosition={activePosition}
          />
        )}
      </div>
    </Background>
  )
}

export default GoalSportTemplate
